import React, { createContext, useMemo, useRef } from "react"

import classNames from "classnames"

import { noop } from "shared/lib/noop"

import { useTabBarContext } from "../context/TabBarContext"
import { useTabBarScrollOnDrag } from "../lib/useTabBarScrollOnDrag"
import { useTabBarScrollState } from "../lib/useTabBarScrollState"

export interface TabBarScrollContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  scrollDistance?: number
  children: React.ReactNode
}

export interface TabBarScrollContextType {
  canScrollLeft: boolean
  canScrollRight: boolean
  hasOverflow: boolean
  contentRef?: React.RefObject<HTMLDivElement | null>
  scrollRef?: React.RefObject<HTMLDivElement | null>
  scrollGroupRef?: React.RefObject<HTMLDivElement | null>
  arrowLeftRef?: React.RefObject<HTMLButtonElement | null>
  arrowRightRef?: React.RefObject<HTMLButtonElement | null>
  scrollLeft: () => void
  scrollRight: () => void
  scrollToTab: (element: HTMLElement) => void
}

export const TabBarScrollContext = createContext<TabBarScrollContextType>({
  canScrollLeft: true,
  canScrollRight: true,
  hasOverflow: false,
  scrollLeft: noop,
  scrollRight: noop,
  scrollToTab: noop
})

const TabBarScrollGroupComponent = React.memo(
  ({
    children,
    className,
    scrollDistance = 80,
    ...rest
  }: TabBarScrollContainerProps) => {
    const tabBarContext = useTabBarContext()
    const enableSort = tabBarContext?.enableSort ?? false

    const arrowLeftRef = useRef<HTMLButtonElement>(null)
    const arrowRightRef = useRef<HTMLButtonElement>(null)

    const {
      scrollRef,
      contentRef,
      scrollGroupRef,
      canScrollLeft,
      canScrollRight,
      hasOverflow,
      scrollLeft,
      scrollRight,
      scrollToTab
    } = useTabBarScrollState({ scrollDistance })

    // Auto-scroll when dragging near arrows or edges
    useTabBarScrollOnDrag({
      enabled: enableSort && hasOverflow,
      arrowLeftRef,
      arrowRightRef,
      scrollRef
    })

    const contextValue = useMemo<TabBarScrollContextType>(
      () => ({
        canScrollLeft,
        canScrollRight,
        hasOverflow,
        scrollRef,
        contentRef,
        scrollGroupRef,
        arrowLeftRef,
        arrowRightRef,
        scrollLeft,
        scrollRight,
        scrollToTab
      }),
      [
        scrollRef,
        contentRef,
        scrollGroupRef,
        arrowLeftRef,
        arrowRightRef,
        canScrollLeft,
        canScrollRight,
        hasOverflow,
        scrollLeft,
        scrollRight
      ]
    )

    return (
      <TabBarScrollContext.Provider value={contextValue}>
        <div
          ref={scrollGroupRef}
          {...rest}
          className={classNames("tab-bar__scroll-group", className)}
        >
          {children}
        </div>
      </TabBarScrollContext.Provider>
    )
  }
)

TabBarScrollGroupComponent.displayName = "TabBar.ScrollGroup"

export const TabBarScrollGroup = TabBarScrollGroupComponent
